import { can } from '../utils/memberPermissions';
import React, { useEffect, useRef, useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  MessageCircle,
  Calendar,
  Users,
  Settings,
  Plus,
  LogOut,
  ChevronLeft,
  ChevronDown,
  Building2,
  MoreHorizontal,
  User
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getSettingsTitle } from '../pages/settings/settingsData';
import Sidebar from './Sidebar';

const Layout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuButtonRef = useRef(null);
  const userMenuRef = useRef(null);
  const logoutDialogRef = useRef(null);
  const logoutTriggerRef = useRef(null);
  const previousPath = useRef(location.pathname);
  useEffect(() => {
    if (previousPath.current === location.pathname) return;
    previousPath.current = location.pathname;
    setShowUserMenu(false);
    document.getElementById('main-content')?.focus({ preventScroll: true });
  }, [location.pathname]);
  const [isOnline, setIsOnline] = useState(() =>
    typeof window !== 'undefined' ? window.navigator?.onLine !== false : true
  );

  useEffect(() => {
    const onOnline = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);

    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);

    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  useEffect(() => {
    if (!showUserMenu) return undefined;

    const onPointerDown = (event) => {
      if (
        userMenuRef.current?.contains(event.target) ||
        userMenuButtonRef.current?.contains(event.target)
      ) {
        return;
      }
      setShowUserMenu(false);
    };

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setShowUserMenu(false);
        userMenuButtonRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [showUserMenu]);

  useEffect(() => {
    const dialog = logoutDialogRef.current;
    if (!dialog) return undefined;

    if (showLogoutConfirm) {
      if (typeof dialog.showModal === 'function' && !dialog.open) {
        dialog.showModal();
      } else if (!dialog.open) {
        dialog.setAttribute('open', '');
      }
      dialog.querySelector('[data-autofocus]')?.focus();
      return undefined;
    }

    if (dialog.open && typeof dialog.close === 'function') {
      dialog.close();
    } else if (dialog.open) {
      dialog.removeAttribute('open');
    }
    logoutTriggerRef.current?.focus();
    return undefined;
  }, [showLogoutConfirm]);

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const isSettingsRoute = location.pathname.startsWith('/settings');
  const isTodayRoute = location.pathname === '/';
  const isLeadsRoute = location.pathname === '/leads' || location.pathname.startsWith('/leads/');
  const isInboxRoute = location.pathname.startsWith('/inbox');
  const isInboxConversation = (/^\/inbox\/[^/]+\/?$/.test(location.pathname) && location.pathname !== '/inbox/email') || /^\/inbox\/email\/[^/]+\/?$/.test(location.pathname);
  const containerWidth = isInboxRoute ? 'max-w-[1600px]' : isSettingsRoute || isTodayRoute || isLeadsRoute || location.pathname === '/catalogue' || location.pathname === '/reports' || (location.pathname.startsWith('/customers') || location.pathname.startsWith('/contacts') || location.pathname.startsWith('/organisations')) ? 'max-w-5xl' : 'max-w-lg';

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getPageTitle = () => {
    if (location.pathname === '/more') return 'More';
    if (location.pathname === '/catalogue') return 'Catalogue';
    if (location.pathname.startsWith('/contacts')) return 'Contacts';
    if (location.pathname.startsWith('/organisations')) return 'Organisations';
    if (location.pathname.startsWith('/customers')) return 'Existing records';
    if (isInboxRoute) return 'Inbox';
    if (location.pathname === '/reports') return 'Reports';
    if (location.pathname === '/') return 'Today';
    if (location.pathname === '/leads') return 'Enquiries';
    if (location.pathname === '/leads/new') return 'New enquiry';
    if (location.pathname.startsWith('/leads/')) return 'Enquiry details';
    if (location.pathname === '/settings') return 'Settings';
    if (location.pathname.startsWith('/settings/')) return getSettingsTitle(location.pathname);
    return 'LeadMarka';
  };

  const showBackButton = !isInboxRoute && (location.pathname.startsWith('/leads/') || location.pathname.startsWith('/settings/'));

  const pageTitle = getPageTitle();

  const closeLogoutDialog = () => {
    setShowLogoutConfirm(false);
  };

  return (
    <div className={`${isInboxConversation ? 'app-inbox-conversation ' : ''}${isInboxRoute ? "flex h-dvh min-h-0 flex-col overflow-hidden bg-gray-50" : "min-h-screen bg-gray-50"}`}>
      <a href="#main-content" className="app-skip-link">Skip to content</a>
      {/* Desktop Sidebar */}
      <div className="hidden lg:block">
        <Sidebar />
      </div>

      <header className="app-mobile-header shrink-0 sticky top-0 z-40 border-b border-gray-200 bg-white safe-area-top lg:fixed lg:left-64 lg:right-0 lg:z-30">
        <div className="h-16 px-4 lg:px-6 flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            {showBackButton && <button aria-label="Go back" onClick={() => navigate(location.pathname.startsWith('/settings/') ? '/settings' : '/leads')} className="rounded-lg p-2 lg:hidden"><ChevronLeft className="h-5 w-5" /></button>}
            {!showBackButton && <img src={`${process.env.PUBLIC_URL}/leadmarka_logo-color.svg`} alt="" className="h-7 w-7 shrink-0 lg:hidden" />}
            <h1 className="truncate text-lg font-semibold text-gray-900">{pageTitle}</h1>
          </div>
          {/* Right - Utilities */}
          <div className="flex items-center gap-2">
            {/* New Button */}
            {can(user, 'createEnquiries') && <Link
              to="/leads/new"
              aria-label="New enquiry"
              className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-primary-700 transition-all shadow-sm"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">New enquiry</span>
            </Link>}

            {/* User Menu */}
            <div className="relative">
              <button
                ref={userMenuButtonRef}
                aria-label="Account menu"
                aria-expanded={showUserMenu}
                aria-controls="account-menu"
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-2 p-1.5 pr-3 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                  <User className="w-4 h-4 text-primary-600" />
                </div>
                <ChevronDown className="w-4 h-4 text-gray-400" />
              </button>

              {/* User Dropdown */}
              {showUserMenu && (
                <div
                  id="account-menu"
                  ref={userMenuRef}
                  className="absolute right-0 top-full mt-1 w-64 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50"
                >
                  {/* User Info */}
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="font-semibold text-gray-900 truncate">{user?.fullName}</p>
                    <p className="text-sm text-gray-500 truncate">{user?.email}</p>
                  </div>

                  {/* Current workspace */}
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Current workspace · {user?.role === 'owner' ? 'Owner' : 'Member'}</p>
                    <Link to="/settings/workspace" onClick={() => setShowUserMenu(false)} className="flex min-h-[44px] items-center gap-2 p-2 rounded-lg bg-primary-50 hover:bg-primary-100">
                      <Building2 className="w-4 h-4 text-primary-600" />
                      <span className="text-sm font-medium text-gray-900 truncate">
                        {user?.workspaceCompanyName || (user?.role === 'owner' && user?.businessName) || 'Your workspace'}
                      </span>
                    </Link>
                  </div>

                  {/* Menu Items */}
                  <Link
                    to="/settings"
                    className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    onClick={() => setShowUserMenu(false)}
                  >
                    <Settings className="w-4 h-4" />
                    Settings
                  </Link>
                  <button
                    aria-label="Log out"
                    onClick={() => {
                      setShowUserMenu(false);
                      logoutTriggerRef.current = userMenuButtonRef.current;
                      setShowLogoutConfirm(true);
                    }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                  >
                    <LogOut className="w-4 h-4" />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        {!isOnline && <p role="status" className="border-t border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900">You're offline. Some data may be outdated.</p>}
      </header>

      {/* Main Content */}
      <main id="main-content" tabIndex={-1} className={`app-main lg:ml-64 ${isOnline ? 'lg:pt-16' : 'lg:pt-28'} ${isInboxRoute ? 'app-main-inbox flex min-h-0 flex-1 flex-col overflow-hidden' : ''}`}>
        <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
          Loaded {pageTitle}
        </div>
        {isInboxConversation && !isOnline && <p role="status" className="lg:hidden shrink-0 bg-amber-50 px-3 py-2 text-sm text-amber-900">You're offline. Some data may be outdated.</p>}
        {/* Mobile Navigation - Only show on mobile */}
        <nav aria-label="Main navigation" className="app-mobile-nav lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50 safe-area-bottom">
          <div className="mx-auto flex h-16 w-full items-center justify-around">
            {[
              { path: '/', label: 'Today', icon: Calendar },
              ...((user?.whatsappInboxEnabled || user?.emailInboxEnabled) ? [{ path: '/inbox', label: 'Inbox', icon: MessageCircle }] : []),
              { path: '/leads', label: 'Enquiries', icon: Users },
              { path: '/more', label: 'More', icon: MoreHorizontal },
            ].map(({ path, label, icon: Icon }) => {
              const active = path === '/more' ? ['/more', '/contacts', '/organisations', '/catalogue', '/reports', '/settings'].some(isActive) : isActive(path);
              return <Link key={path} to={path} aria-current={active ? 'page' : undefined}
                className={`flex flex-col items-center justify-center flex-1 h-full ${active ? 'text-primary-600' : 'text-gray-500'}`}>
                <Icon className="w-5 h-5" />
                <span className="text-xs mt-1">{label}</span>
              </Link>;
            })}
          </div>
        </nav>

        {/* Content Area */}
        <div className={`app-content ${containerWidth} mx-auto w-full p-4 lg:p-6 ${isInboxRoute ? 'min-h-0 flex-1 overflow-y-auto' : ''}`}>
          <Outlet />
        </div>
      </main>

      {/* Logout Confirmation Modal */}
      <dialog
        ref={logoutDialogRef}
        aria-labelledby="logout-dialog-title"
        onKeyDown={(event) => {
          if (event.key !== 'Tab') return;
          const buttons = event.currentTarget.querySelectorAll('button:not([disabled])');
          const first = buttons[0];
          const last = buttons[buttons.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        }}
        onCancel={(event) => {
          event.preventDefault();
          closeLogoutDialog();
        }}
        onClose={() => {
          if (showLogoutConfirm) {
            setShowLogoutConfirm(false);
            logoutTriggerRef.current?.focus();
          }
        }}
        className="p-0 rounded-lg shadow-xl backdrop:bg-black/50 w-full max-w-sm"
      >
        <div className="bg-white rounded-lg p-6 w-full">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            <span id="logout-dialog-title">
              Logout?
            </span>
          </h3>
          <p className="text-gray-600 mb-6">
            Are you sure you want to logout?
          </p>
          <div className="flex gap-3">
            <button
              data-autofocus
              onClick={closeLogoutDialog}
              className="flex-1 p-3 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 active:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleLogout}
              className="flex-1 p-3 bg-danger-500 text-white rounded-lg text-sm font-medium hover:bg-danger-600 active:bg-danger-700 transition-colors"
            >
              Logout
            </button>
          </div>
        </div>
      </dialog>
    </div>
  );
};

export default Layout;
